import { Controller } from "@hotwired/stimulus"
import { t } from "../lib/i18n"

export default class extends Controller {
  static targets = ["toggleAllButton", "accountButton", "content"]
  static values = { accountIds: Array }

  connect() {
    this.selectedIds = new Set(this.accountIdsValue.map(String))
    this.updateUI()
    this.applyFilter()
    this.observeContent()
  }

  disconnect() {
    if (this.observer) {
      this.observer.disconnect()
      this.observer = null
    }
  }

  observeContent() {
    if (!this.hasContentTarget) return
    if (this.observer) this.observer.disconnect()

    this.observer = new MutationObserver(() => this.applyFilter())
    this.observer.observe(this.contentTarget, { childList: true, subtree: true })
  }

  contentTargetConnected() {
    this.applyFilter()
    this.observeContent()
  }

  toggleAll() {
    if (this.selectedIds.size === this.accountIdsValue.length) {
      this.selectedIds.clear()
    } else {
      this.selectedIds = new Set(this.accountIdsValue.map(String))
    }
    this.applyFilter()
    this.updateUI()
  }

  toggleAccount(event) {
    const id = String(event.currentTarget.dataset.accountId)
    if (this.selectedIds.has(id)) {
      this.selectedIds.delete(id)
    } else {
      this.selectedIds.add(id)
    }
    this.applyFilter()
    this.updateUI()
  }

  applyFilter() {
    if (!this.hasContentTarget || !this.selectedIds) return

    const cards = this.contentTarget.querySelectorAll("[data-account-id]")
    let anyVisible = false
    cards.forEach(card => {
      const visible = this.selectedIds.has(card.dataset.accountId)
      card.style.display = visible ? "" : "none"
      if (visible) anyVisible = true
    })

    // "Unselect All" used to just hide every card with no explanation —
    // show a message instead of a blank list (L23).
    this.toggleEmptyFilterMessage(cards.length > 0 && !anyVisible)
  }

  toggleEmptyFilterMessage(show) {
    const existing = this.contentTarget.querySelector("[data-interactions-filter-empty]")

    if (!show) {
      existing?.remove()
      return
    }
    if (existing) return

    const message = document.createElement("div")
    message.dataset.interactionsFilterEmpty = "true"
    message.className = "border rule p-12 text-center"

    const heading = document.createElement("h3")
    heading.className = "condensed text-[22px] font-bold uppercase tracking-[0.06em] text-gray-900 dark:text-gray-100"
    heading.textContent = t("js.interactions_filter.none_selected_title")

    const body = document.createElement("p")
    body.className = "mt-2 text-sm text-gray-500 dark:text-gray-400"
    body.textContent = t("js.interactions_filter.none_selected_body")

    message.append(heading, body)
    this.contentTarget.prepend(message)
  }

  updateUI() {
    const allSelected = this.selectedIds.size === this.accountIdsValue.length

    if (this.hasToggleAllButtonTarget) {
      this.toggleAllButtonTarget.textContent = allSelected ? t("js.interactions_filter.unselect_all") : t("js.interactions_filter.select_all")
    }

    this.accountButtonTargets.forEach(btn => {
      const id = String(btn.dataset.accountId)
      const active = this.selectedIds.has(id)
      // Selected reads as an engraved plate that is lit; unselected is the same
      // plate, dark. Amber is not used here: a filter being on is not an action
      // waiting to be taken.
      btn.classList.toggle("border-gray-900", active)
      btn.classList.toggle("dark:border-gray-100", active)
      btn.classList.toggle("text-gray-900", active)
      btn.classList.toggle("dark:text-gray-100", active)
      btn.classList.toggle("border-gray-300", !active)
      btn.classList.toggle("dark:border-[color:var(--color-rule-dark)]", !active)
      btn.classList.toggle("text-gray-500", !active)
      btn.classList.toggle("dark:text-gray-400", !active)
      btn.classList.toggle("opacity-60", !active)
    })
  }
}
